// Ledger tab. Four stacked sections (each conditionally rendered):
//
//   Expected       (encounter scope, only when no charge_lines yet)
//     ↳ derived from EligibilitySnapshot's patient responsibility breakdown.
//       Surfaces predicted copay/coinsurance/deductible before the first
//       charge_line lands. Hides as soon as Realized/Receipt has data.
//
//   Open invoices  (when any are open in the active scope)
//     ↳ statements / invoices the patient hasn't paid yet. Each row offers
//       an inline "Send reminder" link that bumps the BE's reminder count.
//
//   Activity       (always)
//     ↳ chronological ledger entries from /balance via PatientFinancialSummary.
//       Patient scope shows entries from all encounters with encounter labels.

import type { ActivityLine } from '@features/billing/components/balance-table/rows';
import { formatDeliveryMethod } from '@features/billing/components/v2/shared';
import { useReverseChargeLine } from '@features/billing/store/hooks';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import {
  CopyInvoiceLinkIconButton,
  useReconcileInvoice,
  useSendInvoiceReminder,
} from '@features/payments';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { FeatureGates, useFeatureGate } from '@services/statsig';
import type { ChargeLinePreviewResponse } from '@store/generated/billing-v2';
import { useSnackbar } from 'notistack';

import type { EncounterLedgerGroup, LedgerEntryItem, PatientInvoiceHistoryItem } from '../types';

interface Props {
  patientId: string;
  entries: Array<LedgerEntryItem & { encounterLabel?: string | null }>;
  /** Collapsed lifecycle lines (gdn-2941) — the gated drawer's replacement
   * for the raw entry list in encounter scope. */
  activityLines?: ActivityLine[];
  encounter?: EncounterLedgerGroup;
  preview?: ChargeLinePreviewResponse;
  openInvoices: PatientInvoiceHistoryItem[];
  staleInvoiceIds: string[];
  isEncounterScope: boolean;
  /** Show encounter label on each row (patient scope only). */
  showEncounterLabels: boolean;
}

export const LedgerTabContent = ({
  patientId,
  entries,
  activityLines = [],
  encounter,
  preview,
  openInvoices,
  staleInvoiceIds,
  isEncounterScope,
  showEncounterLabels,
}: Props) => {
  // GDN-2944: gated, the drawer is the action surface — history reads in
  // the same collapsed grammar as the page's Activity table, and the
  // patient-scoped list retires entirely (the table is right behind us).
  const gated = useFeatureGate(FeatureGates.PATIENT_BALANCE_TABLE);
  const hasReceipt = (preview?.encounters ?? []).some(
    encounter => (encounter.lines ?? []).length > 0,
  );

  // Expected hides when Receipt has data (the realized lines supersede the
  // predicted breakdown). Also hidden in patient scope.
  const showExpected = isEncounterScope && !hasReceipt;
  const expected = showExpected ? deriveExpected(encounter) : [];

  return (
    <Box>
      {expected.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <SectionTitle>Expected</SectionTitle>
          {expected.map(item => (
            <Box
              key={item.id}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1,
                px: 1.5,
                mb: 0.5,
                borderRadius: 1,
                bgcolor: ELIG_TOKENS.amberTint,
                border: `1px dashed ${ELIG_TOKENS.lineStrong}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography
                  variant="body2"
                  sx={{ color: ELIG_TOKENS.inkSoft, fontStyle: 'italic' }}
                >
                  {item.label}
                </Typography>
                <Tooltip title={item.rationale} arrow>
                  <InfoOutlinedIcon sx={{ fontSize: 14, color: ELIG_TOKENS.inkFaint }} />
                </Tooltip>
              </Box>
              <Typography variant="body2" sx={{ color: ELIG_TOKENS.ink, fontWeight: 500 }}>
                {dollars(item.amount_cents)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {openInvoices.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <SectionTitle>Open invoices ({openInvoices.length})</SectionTitle>
          {openInvoices.map(invoice => (
            <OpenInvoiceRow
              key={invoice.invoice_id}
              patientId={patientId}
              invoice={invoice}
              isStale={staleInvoiceIds.includes(invoice.invoice_id)}
            />
          ))}
        </Box>
      )}

      {gated && !isEncounterScope ? (
        <Typography
          variant="caption"
          sx={{ color: ELIG_TOKENS.inkFaint, fontStyle: 'italic', display: 'block', py: 1 }}
        >
          History lives in the Activity table — download the account history
          for the full entry-by-entry ledger.
        </Typography>
      ) : gated && isEncounterScope ? (
        <>
          <SectionTitle>Activity</SectionTitle>
          {activityLines.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ color: ELIG_TOKENS.inkFaint, fontStyle: 'italic', py: 1 }}
            >
              No charges or payments yet.
            </Typography>
          ) : (
            activityLines.map(line => <LifecycleRow key={line.id} line={line} />)
          )}
        </>
      ) : (
        <>
          <SectionTitle muted={hasReceipt}>{hasReceipt ? 'Ledger activity' : 'Confirmed'}</SectionTitle>
          {entries.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ color: ELIG_TOKENS.inkFaint, fontStyle: 'italic', py: 1 }}
            >
              No charges or payments yet.
            </Typography>
          ) : (
            entries.map(entry => (
              <ConfirmedRow key={entry.entry_id} entry={entry} showEncounter={showEncounterLabels} />
            ))
          )}
        </>
      )}
    </Box>
  );
};

// ─── Lifecycle row (gated, gdn-2944) ─────────────────────────────────────────
//
// One business event per row, unsigned amount + tone — the drawer-side twin
// of the page table's grammar. Reverse re-homes here: every
// charge-line-backed row offers the verb (paid included — reversing a paid
// charge is the gdn-2915 flow); the server remains the authority on
// eligibility (adjudicated/invoiced lines are rejected with a clear
// message), same coarse filter contract as the raw-entry rows had.

const LIFECYCLE_TONE_COLOR: Record<ActivityLine['tone'], string> = {
  ok: '#16A34A',
  due: '#D97706',
  info: ELIG_TOKENS.inkFaint,
};

const LifecycleRow = ({ line }: { line: ActivityLine }) => {
  const { reverseChargeLine, isLoading: isReversing } = useReverseChargeLine();
  const { enqueueSnackbar } = useSnackbar();
  // Any charge-line-backed row offers the verb — reversing a PAID charge
  // is the original gdn-2915 flow (payment stays, money becomes account
  // credit). The server remains the eligibility authority; ineligible
  // lines get its clear rejection message.
  const reversibleChargeLineId = line.id.startsWith('charge_line:')
    ? line.id.slice('charge_line:'.length)
    : null;
  const handleReverse = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!reversibleChargeLineId || isReversing) return;
    const result = await reverseChargeLine(reversibleChargeLineId);
    if (result.success) {
      enqueueSnackbar(
        `Charge reversed — ${dollars(result.data.amount_cents)} back on the balance`,
        { variant: 'success' },
      );
    } else {
      const detail = (result.error as { data?: { message?: string } })?.data
        ?.message;
      enqueueSnackbar(detail ?? 'Could not reverse this charge', {
        variant: 'error',
      });
    }
  };
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 1,
        py: 1,
        borderBottom: `1px solid ${ELIG_TOKENS.line}`,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: ELIG_TOKENS.ink, fontSize: '0.8125rem' }}>
          {line.label}
        </Typography>
        {line.detail && (
          <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
            {line.detail}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            fontWeight: 600,
            fontSize: '0.8125rem',
            color: LIFECYCLE_TONE_COLOR[line.tone],
          }}
        >
          {dollars(line.amount_cents)}
        </Typography>
        {reversibleChargeLineId && (
          <Tooltip title="Reverse this charge" arrow>
            <IconButton
              size="small"
              aria-label="Reverse charge"
              onClick={handleReverse}
              disabled={isReversing}
              sx={{ color: ELIG_TOKENS.inkFaint, p: 0.25 }}
            >
              <UndoIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

// ─── Open invoice row ────────────────────────────────────────────────────────

const OpenInvoiceRow = ({
  patientId,
  invoice,
  isStale,
}: {
  patientId: string;
  invoice: PatientInvoiceHistoryItem;
  isStale: boolean;
}) => {
  const { sendReminder, isLoading } = useSendInvoiceReminder();
  const { reconcileInvoice, isLoading: isReconciling } = useReconcileInvoice();
  const sentDate = new Date(invoice.created_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const handleRemind = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLoading || isReconciling) return;
    void sendReminder(invoice.invoice_id);
  };
  const handleReconcile = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLoading || isReconciling) return;
    void reconcileInvoice(invoice.invoice_id, {
      action: 'void_invoice',
      sendNotification: false,
    });
  };
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 1,
        borderBottom: `1px solid ${ELIG_TOKENS.line}`,
      }}
    >
      <Box>
        <Typography variant="body2" sx={{ color: ELIG_TOKENS.ink, fontWeight: 600 }}>
          {dollars(invoice.amount_cents)}
        </Typography>
        <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
          Sent {sentDate}
          {invoice.delivery_method ? ` via ${formatDeliveryMethod(invoice.delivery_method)}` : ''}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
        <CopyInvoiceLinkIconButton
          patientId={patientId}
          invoiceId={invoice.invoice_id}
          fallbackUrl={invoice.payment_url}
          disabled={isLoading || isReconciling}
        />
        {isStale && (
          <InvoiceTextButton onClick={handleReconcile} disabled={isLoading || isReconciling}>
            {isReconciling ? 'Voiding…' : 'Void invoice'}
          </InvoiceTextButton>
        )}
        <InvoiceTextButton onClick={handleRemind} disabled={isLoading || isReconciling || isStale}>
          {isLoading ? 'Sending…' : 'Send reminder'}
        </InvoiceTextButton>
      </Box>
    </Box>
  );
};

const InvoiceTextButton = ({
  children,
  disabled,
  onClick,
}: {
  children: React.ReactNode;
  disabled?: boolean;
  onClick: (e: React.MouseEvent) => void;
}) => (
  <Typography
    component="button"
    onClick={e => {
      if (disabled) return;
      onClick(e);
    }}
    aria-disabled={disabled}
    sx={{
      background: 'transparent',
      border: 0,
      cursor: disabled ? 'default' : 'pointer',
      color: disabled ? ELIG_TOKENS.inkFaint : ELIG_TOKENS.midnight,
      fontWeight: 600,
      fontSize: '0.75rem',
      textTransform: 'none',
      p: 0,
      '&:hover': disabled ? {} : { textDecoration: 'underline' },
    }}
  >
    {children}
  </Typography>
);

// ─── Activity row (chronological) ────────────────────────────────────────────

const ConfirmedRow = ({
  entry,
  showEncounter,
}: {
  entry: LedgerEntryItem & { encounterLabel?: string | null };
  showEncounter: boolean;
}) => {
  const isCredit = entry.amount_cents < 0;
  const date = new Date(entry.created_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const { reverseChargeLine, isLoading: isReversing } = useReverseChargeLine();
  const { enqueueSnackbar } = useSnackbar();
  // Manually captured patient charges carry their charge_line_id as the
  // source ref — those are the (only) rows the reverse verb acts on
  // (gdn-2915). Adjudicated/invoiced lines are rejected server-side with a
  // clear message, so eligibility here stays a coarse filter.
  const reversibleChargeLineId =
    entry.entry_type === 'charge' && entry.party_type === 'patient'
      ? entry.source_ref?.id ?? null
      : null;
  const handleReverse = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!reversibleChargeLineId || isReversing) return;
    const result = await reverseChargeLine(reversibleChargeLineId);
    if (result.success) {
      enqueueSnackbar(
        `Charge reversed — ${dollars(result.data.amount_cents)} back on the balance`,
        { variant: 'success' },
      );
    } else {
      const detail = (result.error as { data?: { message?: string } })?.data
        ?.message;
      enqueueSnackbar(detail ?? 'Could not reverse this charge', {
        variant: 'error',
      });
    }
  };
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        py: 1,
        borderBottom: `1px solid ${ELIG_TOKENS.line}`,
      }}
    >
      <Box>
        <Typography variant="body2" sx={{ color: ELIG_TOKENS.ink }}>
          {entry.description ?? entry.entry_type_label}
        </Typography>
        <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
          {date}
          {showEncounter && entry.encounterLabel ? ` · ${entry.encounterLabel}` : ''}
          {' · '}
          {entry.entry_type_label}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, ml: 2 }}>
        <Typography
          variant="body2"
          sx={{
            color: isCredit ? ELIG_TOKENS.green : ELIG_TOKENS.ink,
            fontWeight: 600,
          }}
        >
          {isCredit ? dollars(entry.amount_cents) : `+${dollars(entry.amount_cents)}`}
        </Typography>
        {reversibleChargeLineId && (
          <Tooltip title="Reverse charge — voids it and returns any payment as account credit">
            <span>
              <IconButton
                aria-label="Reverse charge"
                size="small"
                onClick={handleReverse}
                disabled={isReversing}
                sx={{ ml: 0.5, color: ELIG_TOKENS.inkFaint }}
              >
                <UndoIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

// ─── Expected derivation ─────────────────────────────────────────────────────

interface ExpectedItem {
  id: string;
  label: string;
  amount_cents: number;
  rationale: string;
}

const deriveExpected = (encounter: EncounterLedgerGroup | undefined): ExpectedItem[] => {
  if (!encounter) return [];
  const breakdown = encounter.patient_responsibility_breakdown;
  if (!breakdown) return [];
  const items: ExpectedItem[] = [];
  if (breakdown.copay_cents) {
    items.push({
      id: 'copay',
      label: 'Copay (predicted)',
      amount_cents: breakdown.copay_cents,
      rationale: 'From eligibility snapshot',
    });
  }
  if (breakdown.coinsurance_cents) {
    items.push({
      id: 'coinsurance',
      label: 'Coinsurance (predicted)',
      amount_cents: breakdown.coinsurance_cents,
      rationale: 'From eligibility snapshot',
    });
  }
  if (breakdown.deductible_cents) {
    items.push({
      id: 'deductible',
      label: 'Deductible (predicted)',
      amount_cents: breakdown.deductible_cents,
      rationale: 'From eligibility snapshot',
    });
  }
  return items;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const SectionTitle = ({
  children,
  muted = false,
}: {
  children: React.ReactNode;
  muted?: boolean;
}) => (
  <Typography
    variant="overline"
    sx={{
      display: 'block',
      color: muted ? ELIG_TOKENS.inkFaint : ELIG_TOKENS.midnight,
      fontWeight: 600,
      mb: 1,
      letterSpacing: 0.5,
    }}
  >
    {children}
  </Typography>
);

const dollars = (cents: number): string => {
  const abs = Math.abs(cents);
  const sign = cents < 0 ? '−' : '';
  if (abs % 100 === 0) return `${sign}$${abs / 100}`;
  return `${sign}$${(abs / 100).toFixed(2)}`;
};
